import { useEffect, useId, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import IconButton from 'components/priority/IconButton/IconButton'
import { useIsSuperAdmin } from '@/hooks/utility/useIsSuperAdmin'
import { normalizeAdminPath } from '@/components/admin/adminPages'
import { AdminMenuPanel } from './AdminMenuPanel'
import * as S from './AdminMenu.styled'

/**
 * The admin bar's hamburger and the side menu of admin tools it opens. Shown
 * to super admins only. The menu is a non-modal panel: it closes on the
 * hamburger, on Escape, on a click or a focus move outside it, and on any
 * link inside it.
 */
export const AdminMenu = () => {
  const isSuperAdmin = useIsSuperAdmin()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLSpanElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const panelId = useId()

  const focusHamburger = () =>
    buttonRef.current?.querySelector('button')?.focus()

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      focusHamburger()
    }
    // The panel is portalled to the end of the body, so Tab or Shift+Tab out
    // of it lands far from the hamburger; leaving it by keyboard closes it,
    // as a click outside it does. Clicks are read on click, not mousedown, so
    // the press that toggles the hamburger never closes the panel and reopens
    // it in one gesture.
    const closeOutside = (event: Event) => {
      const target = event.target as Node
      if (
        panelRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      )
        return
      setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('click', closeOutside)
    document.addEventListener('focusin', closeOutside)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('click', closeOutside)
      document.removeEventListener('focusin', closeOutside)
    }
  }, [open])

  if (!isSuperAdmin) return null

  // A link to another page changes the page; a link to this one leaves it in
  // place, so focus goes back to the hamburger.
  const onSelect = (path: string) => {
    setOpen(false)
    if (normalizeAdminPath(path) === normalizeAdminPath(pathname))
      focusHamburger()
  }

  return (
    <>
      <S.MenuButton ref={buttonRef} data-testid="admin-menu-button">
        <IconButton
          icon="bars3Icon"
          fill="gray1"
          aria-label={open ? 'Close admin menu' : 'Open admin menu'}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((value) => !value)}
        />
      </S.MenuButton>
      {open && (
        <AdminMenuPanel ref={panelRef} id={panelId} onSelect={onSelect} />
      )}
    </>
  )
}
